import { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  VideoIcon,
  MapPin,
  CircleChevronLeft,
  CircleChevronRight,
  Loader2,
} from "lucide-react";
import doctorAvatar from "../../assets/images/default_avatar.jpg";
import { PatientService } from "../../services/api/patient.service";
import { cn } from "@/lib/utils";

interface Appointment {
  date: string;
  startTime: string;
  doctorDetails: {
    name: string;
    education: string;
    specialization: string;
    profileImage: string;
  };
  clinicDetails: {
    name: string;
    address: string;
  };
  appointmentDetails: {
    status: string;
  };
}

export function PatientsUpcomingAppointments(patientProp: any) {
  const [selectedDate, setSelectedDate] = useState();
  const [appointmentDates, setAppointmentDates] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [selectedAppointments, setSelectedAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUpcomingAppointments = async () => {
      try {
        setLoading(true);
        const response = await PatientService.getPatientHistoryForAppointment(
          patientProp.patient?.userId || patientProp.patient?.patientId
        );
        console.log("UpcomingAppointments", response);
        const fullHistory = response.data.patientAppointmentHistory;

        // Get current date at the start of the day
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Filter appointments with status "Booked" AND date today or in the future
        const bookedAppointments = fullHistory.filter((appointment) => {
          const appointmentDate = new Date(appointment.date);
          appointmentDate.setHours(0, 0, 0, 0);

          return (
            appointment.appointmentDetails?.status === "Booked" &&
            appointmentDate >= today
          );
        });

        // Sort appointments by date (earliest first)
        bookedAppointments.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Extract dates from the booked appointments
        const dates = [...new Set(bookedAppointments.map(a => a.date))];
        setAppointmentDates(dates);

        setUpcomingAppointments(bookedAppointments);

        if (bookedAppointments.length > 0) {
          const firstDate = bookedAppointments[0].date;
          const appointmentsForFirstDate = bookedAppointments.filter(
            (appointment) => appointment.date === firstDate
          );
          setSelectedAppointments(appointmentsForFirstDate);
          setSelectedDate(firstDate);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchUpcomingAppointments();
  }, [patientProp]);

  const formatDate = (date) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const formatTime = (timeString) => {
    // Convert 24-hour time format to 12-hour with AM/PM
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const appointmentsForDate = upcomingAppointments.filter(
      (appointment) => appointment.date === date
    );
    setSelectedAppointments(appointmentsForDate);
  };

  if (loading) {
    return (
      <div className="w-full min-h-[400px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          <p className="text-gray-600 text-sm">Loading your appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-[#771D51]">
          Upcoming Appointments
        </h2>

        <div className="w-full sm:w-64">
          <select
            value={selectedDate || ""}
            onChange={(e) => handleDateClick(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-[#771D51]/20 focus:border-[#771D51] transition-colors"
          >
            <option value="" disabled>Select appointment date</option>
            {appointmentDates.map((date, index) => (
              <option key={index} value={date}>
                {isToday(date) ? "Today" : formatDate(date)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Appointments Grid */}
      {selectedAppointments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {selectedAppointments.map((appointment: Appointment, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              {/* Appointment Time Banner */}
              <div className="bg-[#771D51]/5 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-[#771D51]" />
                    <span className="text-sm font-medium text-gray-900">
                      {isToday(appointment.date) ? "Today" : formatDate(appointment.date)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-medium text-gray-900">
                      {formatTime(appointment.startTime)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Doctor Info */}
              <div className="p-4">
                <div className="flex items-start space-x-4">
                  <img
                    src={appointment.doctorDetails?.profileImage || doctorAvatar}
                    alt={appointment.doctorDetails?.name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {appointment.doctorDetails?.name || "Dr. Name"}
                    </p>
                    {appointment.doctorDetails?.specialization && (
                      <p className="mt-1 text-sm text-gray-500">
                        {appointment.doctorDetails.specialization}
                      </p>
                    )}
                    {appointment.doctorDetails?.education && (
                      <p className="mt-1 text-xs text-gray-500">
                        {appointment.doctorDetails.education}
                      </p>
                    )}
                  </div>
                </div>

                {/* Clinic Info */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-start space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {appointment.clinicDetails?.name || "Main Clinic"}
                      </p>
                      {appointment.clinicDetails?.address && (
                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                          {appointment.clinicDetails.address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-[#771D51]/10 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-[#771D51]" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No upcoming appointments
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              You don't have any upcoming appointments scheduled. Would you like to book a new appointment with one of our specialists?
            </p>
          </div>
        </div>
      )}
    </div>
  );
}